import React, { useCallback, type ReactNode } from 'react';

import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

/**
 * EAA-1821: the multi-select body every filter menu shows — a scrollable list
 * of checkboxes plus a clear action that only appears once something is
 * selected.
 *
 * It is its own component (rather than baked into `FilterPopover`) because the
 * SAME list has to appear behind two different triggers: a wide readout chip in
 * a filter bar and a 26px funnel in a table header. Copying the list into each
 * trigger is how the two drift into looking different — the failure the kit
 * rule exists to prevent.
 *
 * Domain-free: it renders whatever `{ value, label }` pairs it is handed and
 * never knows what a "credential mode" is.
 */

export interface FilterOptionItem {
    value: string;
    label: ReactNode;
}

export interface FilterOptionListProps extends Styleable {
    options: FilterOptionItem[];
    value: string[];
    onChange: (next: string[]) => void;
    /** Accessible name of the group, e.g. the column's own label. */
    label: string;
    /** Footer action copy. Omit to render no footer at all. */
    clearLabel?: ReactNode;
    /** Scroll cap in px — roughly eight rows by default. */
    maxHeight?: number;
}

export function FilterOptionList({
    options,
    value,
    onChange,
    label,
    clearLabel,
    maxHeight = 280,
    className,
    style,
}: FilterOptionListProps): React.ReactElement {
    const colors = useUIColors();

    const handleToggle = useCallback(
        (option: string) => {
            const next = value.includes(option)
                ? value.filter((entry) => entry !== option)
                : [...value, option];
            onChange(next);
        },
        [value, onChange]
    );

    const handleClear = useCallback(() => onChange([]), [onChange]);

    return (
        <div className={cx('ui-filter-option-list', className)} style={style}>
            <div
                className="ui-filter-option-list__items"
                // EAA-1821: `group`, not `listbox` — the rows are `Checkbox`es
                // (antd renders each as a <label> wrapping a real input), and a
                // checkbox is not a valid child of a listbox. The label
                // association the rows need comes from that <label> for free.
                role="group"
                aria-label={label}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxHeight,
                    overflowY: 'auto',
                }}
            >
                {options.map((option) => (
                    <Checkbox
                        key={option.value}
                        className="ui-filter-option"
                        checked={value.includes(option.value)}
                        onChange={() => handleToggle(option.value)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            padding: '7px 8px',
                            borderRadius: 6,
                            color: colors.text,
                            fontSize: 13,
                        }}
                    >
                        <span
                            className="ui-filter-option__label"
                            style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {option.label}
                        </span>
                    </Checkbox>
                ))}
            </div>
            {clearLabel && value.length > 0 && (
                <div
                    className="ui-filter-option-list__footer"
                    style={{
                        display: 'flex',
                        marginTop: 4,
                        paddingTop: 4,
                        borderTop: `1px solid ${colors.divider}`,
                    }}
                >
                    <Button
                        className="ui-filter-option-list__clear"
                        type="text"
                        size="small"
                        onClick={handleClear}
                        style={{ color: colors.errorText, fontWeight: 600 }}
                    >
                        {clearLabel}
                    </Button>
                </div>
            )}
        </div>
    );
}

export default FilterOptionList;
